<template>
  <div v-if="showMessageBox">
    <div class="flex justify-center text-primary">
      <NuxtImg
        loading="lazy"
        :src="errorMessage ? '/icons/c-info-e.svg' : '/img/c-check.svg'"
        alt="check"
        class="w-[44px] h-[44px]"
        width="32"
        height="32"
      />
    </div>
    <div class="space-y-10 mt-2">
      <h3 class="text-center text-dark-blue text-2xl">
        {{ errorMessage ? errorMessage : $t("contact.successTitle") }}
      </h3>
      <p class="text-dark-blue leading-[30px] sm:text-center md:w-3/6 mx-auto" v-if="!errorMessage">
        {{ $t("contact.successDescription") }}
      </p>
      <div class="flex justify-center">
        <NuxtLink :to="localeHomePath(switchBlogLocale())">
          <BaseButton
            class="w-72 h-11 md:mx-0 mx-auto text-lg"
            :text="`${$t('contact.returnToHomepage')}`"
          ></BaseButton>
        </NuxtLink>
      </div>
    </div>
  </div>
  <div class="w-full" v-else>
    <h3 class="text-center text-xl">{{ $t("contact.formTitle") }}</h3>
    <VForm
      :validation-schema="schema"
      :initial-values="initialValues"
      @submit="onSubmit"
      class="flex flex-col md:space-y-11 max-w-4xl mx-auto mt-10"
    >
      <div class="flex flex-col md:flex-row justify-between">
        <BaseVTextInput
          type="name"
          name="name"
          :debug="debug"
          :label="`${$t('form.name')}`"
          class="h-10 md:w-[45%] lg:w-[400px]"
        />
        <BaseVTextInput
          type="phone"
          name="phone"
          :debug="debug"
          :label="`${$t('form.contactUSPhone')}`"
          class="h-10 md:w-[45%] lg:w-[400px] my-10 md:my-0"
        />
      </div>
      <div class="flex flex-col md:flex-row justify-between">
        <BaseVTextInput
          type="email"
          :debug="debug"
          name="email"
          id="email"
          :label="`${$t('form.email')}`"
          class="h-10 md:w-[45%] lg:w-[400px]"
        />
        <BaseVSelect
          :label="$t('form.enquiry')"
          :placeholder="`${$t('form.enquiry')}`"
          :options="options"
          name="enquiry"
          class="md:my-0 my-10 md:w-[45%] lg:w-[400px]"
        />
      </div>
      <div class="md:mt-10">
        <BaseTextArea :label="`${$t('form.message')}`" name="message" type="text" input-class="h-36" :debug="debug" />
      </div>

      <div class="max-w-4xl w-full mx-auto flex justify-end py-4 md:py-0 sm:mt-6">
        <BaseButton
          class="w-full md:w-[45%] lg:w-[400px]"
          :text="`${$t('form.submitBtn')}`"
          type="submit"
          :loading="showLoader"
        ></BaseButton>
      </div>
    </VForm>
  </div>
</template>
<script setup>
import { load } from "recaptcha-v3";
import { configure } from "vee-validate";
import * as yup from "yup";
import { useGlobalStore } from "~/stores/global";

const globalStore = useGlobalStore();
const showMessageBox = ref(false);
const { t } = useI18n();
const showLoader = ref(false);

const initialValues = {
  name: "",
  email: "",
  phone: "",
  enquiry: "",
  message: "",
  token: "",
  honeypot: "",
};
const debug = ref(false);
const errorMessage = ref();

async function recaptcha() {
  const recaptcha = await load(useRuntimeConfig().public.apiRecaptchaKey, {
    useRecaptchaNet: true,
    autoHideBadge: true,
  });
  const token = await recaptcha.execute("contactUs");
  return token;
}

async function onSubmit(values, { resetForm }) {
  document.getElementById("email").blur();
  showLoader.value = true;
  // get token from recaptcha
  const token = await recaptcha();
  const formData = {
    name: values.name,
    email_address: values.email,
    phone_number: values.phone,
    enquiry_type: values.enquiry,
    your_message: values.message,
    token: token,
    honeypot: "",
  };
  //Submit values to API...
  const { data: result, error: error } = await useSubmitContactForm(formData);
  if (error.value) {
    errorMessage.value = error.value.replaceAll('"', "");
  }
  showMessageBox.value = true;
  showLoader.value = false;
  resetForm();
}

configure({
  validateOnBlur: true,
  validateOnChange: true,
  validateOnInput: true,
  validateOnModelUpdate: true,
});

const schema = yup.object({
  email: yup
    .string()
    .required(t("validationErrors.emailRequired"))
    .email(t("validationErrors.emailValid"))
    .label(t("form.email")),
  name: yup.string().required(t("validationErrors.nameRequired")).label(t("form.name")),
  enquiry: yup.string().required(t("validationErrors.enquiryValid")),
  message: yup.string().required(t("validationErrors.messageRequired")),
});

const options = [
  { value: t("form.businessEnquiry"), text: t("form.businessEnquiry") },
  { value: t("form.patientEnquiry"), text: t("form.patientEnquiry") },
];
</script>
